import { Fragment } from "react";
import type { useFpv } from "../hooks/useFpv";
import type { useRadio } from "../hooks/useRadio";
import type { useFlightTape } from "../hooks/useFlightTape";
import { usePersistent } from "../hooks/usePersistent";
import { useEdgeReveal } from "../hooks/useEdgeReveal";
import { FLIGHT_AXES } from "../fpvRates";
import { NumberField } from "../NumberField";
import { RadioPanel } from "./RadioPanel";
import { Section } from "./Section";

const AXIS_LABEL = { roll: "Roll", pitch: "Pitch", yaw: "Yaw" } as const;

type SecKey = "flight" | "rates" | "world" | "tape" | "radio";

/**
 * The FPV right-hand rail: collapsible sections for flight feel, per-axis
 * rates (simple or Betaflight Actual Rates), the world, flight tapes, and the
 * radio mapper — plus a pin/auto toggle that hides the whole rail until the
 * pointer nears the right edge. Pulled out of SwarmStudio so the shell stays
 * thin as FPV grows.
 */
export function FpvPanel({
  fpv, radio, tape, onReplay,
}: {
  fpv: ReturnType<typeof useFpv>;
  radio: ReturnType<typeof useRadio>;
  tape: ReturnType<typeof useFlightTape>;
  onReplay: (id?: string) => void;
}) {
  const p = fpv.params;
  const actual = p.rateMode === "actual";
  const [open, setOpen] = usePersistent<Record<SecKey, boolean>>("swarmsim_fpvsec_v1", {
    flight: true, rates: false, world: false, tape: false, radio: false,
  });
  const [railAuto, setRailAuto] = usePersistent<boolean>("swarmsim_fpvrail_v1", false);
  const nearRight = useEdgeReveal("right", railAuto);
  const railVisible = !railAuto || nearRight;
  const tog = (k: SecKey) => () => setOpen((s) => ({ ...s, [k]: !s[k] }));

  return (
    <>
      {!railVisible && (
        <button className="menu-tab right" onClick={() => setRailAuto(false)} title="FPV settings auto-hide — slide the pointer to the right edge, or click to pin">⚙</button>
      )}
      <div className={"fpv-right" + (railVisible ? "" : " slid")}>
        <div className="rail-head">
          <span className="rail-title">FPV</span>
          <button
            className={"chip mini" + (railAuto ? " active" : "")}
            onClick={() => setRailAuto(!railAuto)}
            title={railAuto ? "Auto-hide is ON — the rail hides until the pointer nears the right edge" : "Pin the rail open (click to switch to auto-hide)"}
          >{railAuto ? "👻 Auto" : "📌 Pinned"}</button>
        </div>

        <Section className="fpv-sect" title="Flight" summary={"thrust " + p.thrust + " · tilt " + p.tilt + "°"} open={open.flight} onToggle={tog("flight")}>
          <NumberField label="Thrust" value={p.thrust} min={8} max={40} step={1} onChange={fpv.setParam("thrust")} />
          <NumberField label="Gravity" value={p.gravity} min={0} max={20} step={0.5} onChange={fpv.setParam("gravity")} format={(v) => v.toFixed(1)} />
          <NumberField label="Drag" value={p.drag} min={0.1} max={2} step={0.1} onChange={fpv.setParam("drag")} format={(v) => v.toFixed(1)} />
          <NumberField label="Cam tilt" value={p.tilt} min={0} max={90} step={1} onChange={fpv.setParam("tilt")} format={(v) => v + "°"} />
          <NumberField label="Fisheye" value={p.fisheye} min={0} max={1} step={0.05} onChange={fpv.setParam("fisheye")} format={(v) => Math.round(v * 100) + "%"} />
        </Section>

        <Section className="fpv-sect" title="Rates" summary={p.rateMode} open={open.rates} onToggle={tog("rates")}>
          <span className="seg rate-seg">
            <button
              className={"chip seg-btn" + (!actual ? " active" : "")}
              onClick={() => fpv.setParam("rateMode")("simple")}
              title="One max rate per axis, linear stick response"
            >Simple</button>
            <button
              className={"chip seg-btn" + (actual ? " active" : "")}
              onClick={() => fpv.setParam("rateMode")("actual")}
              title="Betaflight Actual Rates: centre sensitivity / max rate / expo per axis"
            >Actual</button>
          </span>
          {!actual ? (
            <>
              {FLIGHT_AXES.map((ax) => (
                <NumberField
                  key={ax}
                  label={AXIS_LABEL[ax]}
                  value={p.rates[ax]}
                  min={100} max={1400} step={20}
                  onChange={fpv.setSimpleRate(ax)}
                  format={(v) => v + "°/s"}
                />
              ))}
            </>
          ) : (
            <div className="rates-grid">
              <span className="rates-head" />
              <span className="rates-head">Center</span>
              <span className="rates-head">Max</span>
              <span className="rates-head">Expo</span>
              {FLIGHT_AXES.map((ax) => (
                <Fragment key={ax}>
                  <span className="rates-axis">{AXIS_LABEL[ax]}</span>
                  <NumberField value={p.actual[ax].center} min={10} max={600} step={10} onChange={fpv.setActualRate(ax, "center")} />
                  <NumberField value={p.actual[ax].max} min={100} max={1800} step={10} onChange={fpv.setActualRate(ax, "max")} />
                  <NumberField value={p.actual[ax].expo} min={0} max={1} step={0.01} onChange={fpv.setActualRate(ax, "expo")} format={(v) => v.toFixed(2)} />
                </Fragment>
              ))}
            </div>
          )}
        </Section>

        <Section className="fpv-sect" title="World" summary={(p.ground ? p.groundAlt + " m" : "no floor") + " · ×" + p.worldScale} open={open.world} onToggle={tog("world")}>
          <div className="fpv-chip-row">
            <button
              className={"chip" + (p.ground ? " active" : "")}
              onClick={() => fpv.setParam("ground")(!p.ground)}
              title="Grass floor — touch it and you respawn at the start"
            >🌱 Ground</button>
            <button
              className={"chip" + (p.streaks ? " active" : "")}
              onClick={() => fpv.setParam("streaks")(!p.streaks)}
              title="Red edge streaks when a show drone gets inside the warn distance"
            >⚠ Streaks</button>
          </div>
          {p.ground && (
            <>
              <NumberField label="Altitude" value={p.groundAlt} min={2} max={60} step={1} onChange={fpv.setParam("groundAlt")} format={(v) => v + " m"} />
              <NumberField label="Darkness" value={p.groundDark} min={0} max={1} step={0.05} onChange={fpv.setParam("groundDark")} format={(v) => Math.round(v * 100) + "%"} />
            </>
          )}
          {p.streaks && (
            <NumberField label="Warn dist" value={p.warnDist} min={0.5} max={10} step={0.5} onChange={fpv.setParam("warnDist")} format={(v) => v.toFixed(1) + " m"} />
          )}
          <NumberField
            label="World scale"
            value={p.worldScale}
            min={0.1} max={10} step={0.1}
            onChange={fpv.setParam("worldScale")}
            format={(v) => "×" + v.toFixed(1)}
          />
        </Section>

        <Section
          className="fpv-sect"
          title="Tape"
          summary={tape.recording ? "● REC" : tape.tapes.length ? tape.tapes.length + (tape.tapes.length === 1 ? " tape" : " tapes") : "empty"}
          open={open.tape}
          onToggle={tog("tape")}
        >
          <div className="fpv-chip-row">
            <button
              className={"chip" + (tape.recording ? " rec-on" : "")}
              onClick={() => tape.toggleRecord({ fisheye: p.fisheye, ground: p.ground, groundAlt: p.groundAlt, groundDark: p.groundDark })}
              title={"Record up to " + tape.maxSec + " s of flight as raw data (R key, or map a radio switch)"}
            >{tape.recording ? "■ Stop" : "● Record"}</button>
            {tape.tape && !tape.recording && (
              <button className="chip go" onClick={() => onReplay()} title="Watch the selected tape back — camera, sticks and audio re-rendered live">▶ Replay</button>
            )}
          </div>
          {tape.tapes.length > 0 && !tape.recording && (
            <div className="tape-list">
              {tape.tapes.map((t) => (
                <div
                  key={t.id}
                  className={"tape-row" + (t.id === tape.activeId ? " active" : "")}
                  onClick={() => tape.setActiveId(t.id)}
                  title="Select this tape (replay/share act on the selected one)"
                >
                  <span className="tape-name">{t.name}</span>
                  <button className="chip mini" onClick={(e) => { e.stopPropagation(); onReplay(t.id); }} title="Replay this tape">▶</button>
                  <button className="chip mini" onClick={(e) => { e.stopPropagation(); tape.deleteTape(t.id); }} title="Delete this tape">✕</button>
                </div>
              ))}
              {tape.tapes.length > 1 && (
                <button className="chip mini" onClick={tape.clearTapes} title="Delete every tape in the list">🗑 Clear all</button>
              )}
            </div>
          )}
          {tape.tape && !tape.recording && (
            <div className="fpv-chip-row">
              <button className="chip mini" onClick={tape.copyLink} title="Copy a #flight= link that replays the selected tape on sim.free.vet">🔗 Copy link</button>
              <button className="chip mini" onClick={tape.saveFile} title="Save the selected tape as a .flight file">⬇ .flight</button>
            </div>
          )}
          <label className="chip mini tape-load">
            📂 Load .flight
            <input
              type="file"
              accept=".flight"
              style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) tape.loadFile(f); e.target.value = ""; }}
            />
          </label>
          {tape.status && <div className="radio-hint">{tape.status}</div>}
          <div className="radio-hint">⚠ Tapes live only in this tab — save a .flight or copy a link before you refresh.</div>
        </Section>

        <Section className="fpv-sect" title="Radio" summary={radio.connected ? radio.deviceName : "not connected"} open={open.radio} onToggle={tog("radio")}>
          <RadioPanel radio={radio} embedded />
        </Section>
      </div>
    </>
  );
}
